import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {GlowText} from "../components/dimus/GlowText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {AccentLine} from "../components/dimus/AccentLine";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * CAFÉ COM TEU ROI EP11 — "Cliente que reclama é cliente que ainda liga"
 * Editing Style: DYNAMIC (Dark Premium) — mesma identidade EP08-10
 * Profile: @guilhermeribeiro.ai
 * Evolução: Comparação visual lado a lado, ícone telefone animado, cards contrastantes
 */

const YELLOW = IDV.third;
const GREEN = IDV.secondary;
const RED = IDV.error;
const BLUE = IDV.primary;
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 125, file: "audio/cafe-ep11/cafe_ep11_c01.mp3", dir: "right" as const},
  {dur: 217, file: "audio/cafe-ep11/cafe_ep11_c02.mp3", dir: "left" as const},
  {dur: 750, file: "audio/cafe-ep11/cafe_ep11_c03.mp3", dir: "up" as const},
  {dur: 224, file: "audio/cafe-ep11/cafe_ep11_c04.mp3", dir: "right" as const},
  {dur: 223, file: "audio/cafe-ep11/cafe_ep11_c05.mp3", dir: "up" as const},
];

export const CAFE_EP11_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Particles ────────────────────────────────────────────────────────
const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`cpx11-${i}`) * 1080;
        const baseY = random(`cpy11-${i}`) * 1920;
        const size = 2 + random(`cps11-${i}`) * 3;
        const speed = 0.15 + random(`cpsp11-${i}`) * 0.3;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.06 + random(`cpo11-${i}`) * 0.1,
          }} />
        );
      })}
    </>
  );
};

// ─── Pill badge (from Café series) ───────────────────────────────────
const Pill: React.FC<{text: string; color: string; delay: number; icon?: string}> = ({text, color, delay, icon}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});
  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `scale(${interpolate(p, [0, 1], [0.7, 1], CLAMP)})`,
      padding: "10px 20px", borderRadius: 999, display: "flex", alignItems: "center", gap: 8,
      background: `${color}18`, border: `1px solid ${color}44`,
    }}>
      {icon && <span style={{fontSize: 20}}>{icon}</span>}
      <span style={{fontSize: FONT_SCALE.label.min, fontWeight: 700, color, fontFamily: FONT_STACK}}>{text}</span>
    </div>
  );
};

// ─── Comparison Card ─────────────────────────────────────────────────
const CompareCard: React.FC<{
  title: string; subtitle: string; color: string; icon: string;
  delay: number; highlight?: boolean;
}> = ({title, subtitle, color, icon, delay, highlight}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.heavy});
  const glow = highlight && frame > delay + 20 ? 1 + Math.sin(frame * 0.06) * 0.03 : 1;

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [40, 0], CLAMP)}px) scale(${glow})`,
      padding: "28px 24px", borderRadius: 20, width: "85%",
      background: highlight ? `${color}12` : "rgba(255,255,255,0.03)",
      border: `2px solid ${highlight ? `${color}66` : "rgba(255,255,255,0.06)"}`,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
      boxShadow: highlight ? `0 0 30px ${color}20` : "none",
    }}>
      <div style={{fontSize: 48}}>{icon}</div>
      <div style={{fontSize: FONT_SCALE.h2.min, fontWeight: 800, color, fontFamily: FONT_STACK, textAlign: "center"}}>
        {title}
      </div>
      <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3}}>
        {subtitle}
      </div>
    </div>
  );
};

// ─── SCENE 1: ABERTURA RITUAL ─────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 16}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <Pill text="☕ EP11" color={YELLOW} delay={5} />
    <GradientText text="CAFÉ COM TEU ROI" colors={[YELLOW, GREEN]} fontSize={FONT_SCALE.h1.min} springProfile="bouncy" />
    <div style={{marginTop: 8}}>
      <AccentLine delay={30} color={YELLOW} width={200} />
    </div>
  </AbsoluteFill>
);

// ─── SCENE 2: PRINCÍPIO DO DIA ───────────────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const principleP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const contrastP = spring({frame: frame - 80, fps, config: SPRINGS.smooth});

  return (
    <OrganicLayer intensity="subtle" seed="cafe-ep11">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 24}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <Pill text="PRINCÍPIO DO DIA" color={GREEN} delay={5} icon="💡" />
      <div style={{
        opacity: principleP,
        transform: `translateY(${interpolate(principleP, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
        maxWidth: "88%",
      }}>
        Cliente que reclama{"\n"}é cliente que ainda liga.
      </div>
      <div style={{
        opacity: contrastP,
        transform: `translateY(${interpolate(contrastP, [0, 1], [15, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: RED,
        fontFamily: FONT_STACK, textAlign: "center", fontStyle: "italic",
      }}>
        O problema é o que vai embora calado.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: REFLEXÃO — comparação visual ────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();

  // Fase A (0-300): Texto de reflexão
  const textP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const text2P = spring({frame: frame - 120, fps, config: SPRINGS.smooth});

  // Fase B (300-550): Cards de comparação
  const showCards = frame >= 300;

  // Fase C (550+): Frase final
  const finalP = spring({frame: frame - 550, fps, config: SPRINGS.heavy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />

      {/* Fase A: Reflexão texto */}
      {frame < 320 && (
        <>
          <div style={{
            opacity: textP,
            transform: `translateY(${interpolate(textP, [0, 1], [20, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
            fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.4, maxWidth: "88%",
          }}>
            Se o cliente tivesse desistido,{"\n"}ele nem ligava.
          </div>
          <div style={{
            opacity: text2P,
            transform: `translateY(${interpolate(text2P, [0, 1], [20, 0], CLAMP)}px)`,
            fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
            fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2, maxWidth: "88%",
          }}>
            Ele simplesmente sumia.
          </div>
        </>
      )}

      {/* Fase B: Comparação visual */}
      {showCards && frame < 570 && (
        <div style={{display: "flex", flexDirection: "column", gap: 16, alignItems: "center", width: "100%"}}>
          <CompareCard
            title="Reclama"
            subtitle="Ainda quer que funcione"
            color={GREEN}
            icon="📞"
            delay={310}
            highlight
          />
          <div style={{
            fontSize: FONT_SCALE.label.min, fontWeight: 600, color: MUTED,
            fontFamily: FONT_STACK,
            opacity: spring({frame: frame - 400, fps, config: SPRINGS.smooth}),
          }}>vs</div>
          <CompareCard
            title="Sai calado"
            subtitle="Já desistiu de você"
            color={RED}
            icon="🚪"
            delay={360}
          />
        </div>
      )}

      {/* Fase C: Frase impacto */}
      {frame >= 550 && (
        <div style={{
          opacity: finalP, textAlign: "center",
          transform: `scale(${interpolate(finalP, [0, 1], [0.9, 1], CLAMP)})`,
        }}>
          <GlowText text="Me dá um motivo pra ficar." delay={555} size={FONT_SCALE.h2.max} color={GREEN} />
          <div style={{
            opacity: spring({frame: frame - 620, fps, config: SPRINGS.smooth}),
            fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
            fontFamily: FONT_STACK, marginTop: 12,
          }}>
            Diagnóstico de graça.
          </div>
        </div>
      )}
    </AbsoluteFill>
  );
};

// ─── SCENE 4: COMPROMISSO ─────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 10, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 60, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left + 20}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <Pill text="COMPROMISSO" color={YELLOW} delay={5} icon="🤝" />
      <div style={{
        opacity: p1,
        transform: `translateX(${interpolate(p1, [0, 1], [-30, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE,
        fontFamily: FONT_STACK, lineHeight: 1.2, maxWidth: "92%",
      }}>
        Olhar a última reclamação.
      </div>
      <div style={{
        opacity: p2,
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "90%",
      }}>
        Em vez de resolver rápido,{"\n"}entender o que ela tá pedindo.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: DESAFIO + CTA ──────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const challengeP = spring({frame: frame - 8, fps, config: SPRINGS.heavy});
  const profileP = spring({frame: frame - 80, fps, config: SPRINGS.snappy});
  const closeP = spring({frame: frame - 140, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />

      <Pill text="DESAFIO" color={GREEN} delay={5} icon="🎯" />
      <div style={{
        opacity: challengeP,
        transform: `translateY(${interpolate(challengeP, [0, 1], [20, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.body.min, fontWeight: 600, color: WHITE,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3, maxWidth: "88%",
      }}>
        Liga pro último cliente{"\n"}que reclamou.
      </div>

      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 56, height: 56, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${GREEN}`, boxShadow: `0 0 20px ${GREEN}30`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Café com teu ROI</div>
        </div>
      </div>

      <div style={{
        opacity: closeP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, fontStyle: "italic",
      }}>
        Até amanhã.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE FADE ──────────────────────────────────────────────────────
const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const CafeEp11: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 30%, ${BLUE}22, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={20} color={GREEN} />
      <FilmGrain opacity={0.03} />
      <Vignette intensity={0.6} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
